/**
 * RTMPose engine — adapts the canonical RTMPoseAdapter to the legacy
 * PoseEngine interface used by the live UI loop.
 *
 * RTMPose produces 17 COCO keypoints, same layout as MoveNet, so we
 * reuse MOVENET_SKELETON for nodeNames / links / jointDefs.
 *
 * Top-down: this version runs full-frame (no person detector). Multi-
 * subject footage is best served by coupling with the existing
 * YOLOv8n detector in a later iteration.
 *
 * docs/migration-plan.md Phase 11.
 */

import { RTMPoseAdapter, type RTMPoseAdapterOptions } from '../adapters/rtmpose';
import { MOVENET_SKELETON } from './MoveNetSkeleton';
import type {
  PoseEngine,
  PoseSource,
  RawLandmark,
  RawPoseResult,
  SkeletonDefinition,
} from './PoseEngine';

export interface RTMPoseEngineOptions {
  tier: 't' | 'm' | 'l';
  /** Optional override URL. Default: /models/rtmpose-{tier}-coco17.onnx. */
  modelUrl?: string;
  /** Stable hash provided by the host. */
  deviceIdHash?: string;
}

const COCO17_INDEX_COUNT = 17;

/**
 * Adapter→Engine bridge. Owns the RTMPoseAdapter, normalises its
 * canonical pose output back to RawLandmark form so the live HUD /
 * pose record store keep working unchanged.
 */
export class RTMPoseEngine implements PoseEngine {
  readonly id: string;
  readonly label: string;
  readonly skeleton: SkeletonDefinition = MOVENET_SKELETON;
  readonly supportsMultiPose = false;

  private adapter: RTMPoseAdapter;
  private ready = false;

  constructor(opts: RTMPoseEngineOptions) {
    const adapterOpts: RTMPoseAdapterOptions = {
      tier: opts.tier,
      deviceIdHash: opts.deviceIdHash ?? 'unknown',
      modelUrl: opts.modelUrl,
    };
    this.adapter = new RTMPoseAdapter(adapterOpts);
    this.id = `rtmpose-${opts.tier}`;
    this.label = `RTMPose ${opts.tier.toUpperCase()}`;
  }

  async initialize(): Promise<void> {
    if (this.ready) return;
    await this.adapter.initialize();
    await this.adapter.start();
    this.ready = true;
  }

  async setNumPoses(_n: number): Promise<void> {
    // Single-pose only at this scaffold tier; multi-person needs
    // YOLO coupling. No-op preserves the legacy contract.
  }

  async detectForVideo(source: PoseSource, timestampMs: number): Promise<RawPoseResult> {
    if (!this.ready) return { poses: [] };
    const frame = await this.adapter.pollFrame(source, timestampMs);
    if (!frame || frame.pose.bodies.length === 0) return { poses: [] };
    return { poses: frame.pose.bodies.map(canonicalBodyToRawPose) };
  }

  dispose(): void {
    void this.adapter.dispose();
    this.ready = false;
  }
}

/**
 * Project a canonical Body's 17 COCO landmarks back into the legacy
 * RawLandmark[] order (matching MOVENET_NODE_NAMES). RTMPose synthesises
 * spine / pelvis joints; those carry CanonicalJointKey but no native
 * COCO index — we drop them when assembling the legacy view, since
 * MOVENET_SKELETON only references the 17 native indices.
 */
function canonicalBodyToRawPose(body: {
  landmarks: { jointKey: string | null; raw: { nativeIndex: number }; pos2D: { xPx: number; yPx: number; sourceWidthPx: number; sourceHeightPx: number }; confidence: { score: number } }[];
}): { landmarks: RawLandmark[] } {
  const out: RawLandmark[] = new Array(COCO17_INDEX_COUNT).fill(null).map(() => ({
    x: 0,
    y: 0,
    score: 0,
  }));
  for (const lm of body.landmarks) {
    const idx = lm.raw.nativeIndex;
    if (idx < 0 || idx >= COCO17_INDEX_COUNT) continue;
    const w = lm.pos2D.sourceWidthPx || 1;
    const h = lm.pos2D.sourceHeightPx || 1;
    out[idx] = {
      // Legacy contract uses normalised coords (0..1).
      x: lm.pos2D.xPx / w,
      y: lm.pos2D.yPx / h,
      score: lm.confidence.score,
    };
  }
  return { landmarks: out };
}
